import {
  Dimensions,
  LinkApiModel,
  PaymentOptionOptionApiModel,
  PaymentOptionType,
  Service,
  ShipResponse,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { BulkSaveSelectedAction, ISmartSendContext } from '../context/SmartSendContextModels';
import { ApplierOption } from './ApplierOption';
import { SumNumber, ToNumber, onlyUnique } from './ArrayHelper';
import { round2 } from '../parcels/round';
import { RemoveOffset } from './DateHelper';
import { GetSelectedItems } from './GetSelectedItems';
import GenerateOptions from './SmartSendBulkActionOptions';
import { BulkTariffCodes } from '~/apps/smartsend/components/smartsend/actions/BulkTariffCodes';
import { ProtectionExtraIds } from '~/apps/smartsend/components/ExtrasHelper';
import GetTariffSystemFromItem from './GetTariffSystemFromItem';
import { getLocalUnitsEntry, reconcileLocalUnitsEntry } from './LocalUnitsEntryHelper';
import { SplitConsignmentWeight } from '../../components/smartsend/panels/consignments/GetConsignmentFromItem';
import {
  clearLocalUnitsEntryFields,
  localUnitsEntryHasValues,
  LocalUnitsEntry,
  ParcelUnitField
} from './DimensionUnits';

const allParcelUnitFields: ParcelUnitField[] = ['weight', 'length', 'width', 'height'];

const dimensionKeyToParcelField: Record<keyof Dimensions, ParcelUnitField> = {
  Weight: 'weight',
  Length: 'length',
  Width: 'width',
  Height: 'height'
};

const parcelFieldToDimensionKey: Record<ParcelUnitField, keyof Dimensions> = {
  weight: 'Weight',
  length: 'Length',
  width: 'Width',
  height: 'Height'
};

/**
 * Which parcel axes a partial Dimensions payload actually touches. A socket update carries only the
 * axes that changed, and reflowing an axis the payload never mentioned would overwrite a per-parcel
 * value with the item's.
 */
export const dimensionKeysToParcelFields = (dims: Dimensions): ParcelUnitField[] =>
  Object.keys(dims ?? {})
    .map((key) => dimensionKeyToParcelField[key as keyof Dimensions])
    .filter((field): field is ParcelUnitField => !!field);

/**
 * Which axes a full Dimensions object actually MOVES, given what the item held before.
 *
 * The single-action applier hands over a complete Dimensions object, so dimensionKeysToParcelFields
 * reports all four axes and every save that touches dimensions used to overwrite the per-parcel
 * Length/Width/Height as well. Comparing against the previous values narrows it to the real change.
 */
export const changedDimensionFields = (before: Dimensions, after: Dimensions): ParcelUnitField[] =>
  dimensionKeysToParcelFields(after).filter(
    (field) =>
      ToNumber(before?.[parcelFieldToDimensionKey[field]]) !== ToNumber(after?.[parcelFieldToDimensionKey[field]])
  );

/**
 * Push a dimensions change down onto a multi-parcel item's parcels.
 *
 * Without this a weight change updated only PackageDimensions, while GetItemWeight reads the SUM of
 * the parcel weights whenever a consignment exists - so the change appeared to do nothing on exactly
 * the items most likely to need it, and the API kept booking the stale parcel weights. Mirrors the
 * single-item path in ItemDetails.
 *
 * Exported for the socket reducer and the single-action applier, which have the same problem on an
 * incoming Dimensions update.
 *
 * Returns whether it actually touched the parcels, so callers can mark `Consignment` unsaved - without
 * that the reflowed weights live only in the client and the API keeps booking the stale ones.
 */
export const reflowConsignmentDimensions = (item: SmartSendItemApiModel, changedFields: ParcelUnitField[]): boolean => {
  if (!(item.Consignment?.Parcels?.length > 0) || changedFields.length === 0) {
    return false;
  }

  // Weight is the one axis with a SUM relationship to the parcels, so it is the one axis where the change
  // can have come from the other direction. ItemDetails.updateConsignment recomputes the item's weight as
  // GetItemWeight - the sum of the parcels - the moment any parcel is edited, so by the time this runs a
  // per-parcel edit is indistinguishable from an item-level weight change. Pushing it down then re-divides
  // the total evenly and destroys what the user typed.
  //
  // If the item's weight is already the parcels' sum there is nothing to push down, whoever moved it. The
  // cases this reflow exists for - the ITEM-WEIGHT quick fix on zero-weight parcels, a bulk weight change
  // - all have a sum that differs from the new item weight, so they are unaffected.
  const parcelWeightSum = round2(SumNumber(item.Consignment.Parcels.map((p) => p.Dimensions?.Weight)));
  const weightCameFromTheParcels = parcelWeightSum === round2(ToNumber(item.PackageDimensions?.Weight));

  const fields = weightCameFromTheParcels ? changedFields.filter((f) => f !== 'weight') : changedFields;

  if (fields.length === 0) {
    return false;
  }

  // Per axis, rather than spreading the whole of PackageDimensions over each parcel. The old wholesale
  // copy meant a length-only reflow - which is all a socket payload usually carries - also replaced every
  // parcel's weight, then re-divided it. `changedFields` was only ever an all-or-nothing gate.
  //
  // The as-entered entry still goes across wholesale: each parcel now holds the item's values for the
  // axes that moved, so the item's originals are true for them, and a parcel's own prior entry may
  // describe values that no longer exist. SplitConsignmentWeight drops the weight original from any
  // parcel whose share it divided.
  const itemEntry = getLocalUnitsEntry(item.Meta);

  item.Consignment.Parcels.forEach((parcel) => {
    const next: Dimensions = { ...parcel.Dimensions };
    fields.forEach(
      (field) => (next[parcelFieldToDimensionKey[field]] = item.PackageDimensions?.[parcelFieldToDimensionKey[field]])
    );
    parcel.Dimensions = next;
    parcel.LocalUnits = itemEntry ? { ...itemEntry } : undefined;
  });

  if (fields.includes('weight')) {
    SplitConsignmentWeight(item.PackageDimensions, item.Consignment);
  }

  return true;
};

type NumericEntryKey = 'originalWeightLb' | 'originalLengthIn' | 'originalWidthIn' | 'originalHeightIn';

const localUnitsEntryFieldKey: Record<ParcelUnitField, NumericEntryKey> = {
  weight: 'originalWeightLb',
  length: 'originalLengthIn',
  width: 'originalWidthIn',
  height: 'originalHeightIn'
};

/**
 * Merge the bulk action's local-units entry onto an item's own Meta, but only for the axes this
 * action actually applied to that item - `Dimensions:Add`'s "fill missing only" semantics must not
 * stamp an entry value for an axis it left untouched.
 */
function applyLocalUnitsEntry(
  item: SmartSendItemApiModel,
  changedFields: ParcelUnitField[],
  localUnitsEntry?: LocalUnitsEntry
) {
  if (!changedFields.length) {
    return;
  }
  const existing = getLocalUnitsEntry(item.Meta);
  if (!existing && !localUnitsEntryHasValues(localUnitsEntry)) {
    // Nothing to record and nothing pre-existing - don't stamp a no-op empty entry onto Meta.
    return;
  }
  // A metric-sourced bulk edit fully resets the entry (not just the touched axes) - otherwise an
  // untouched axis's stale imperial original could resurface later if a future imperial edit spreads
  // this same entry object and flips unitSystem back to 'imperial'.
  const fieldsToClear = localUnitsEntry?.unitSystem === 'metric' ? allParcelUnitFields : changedFields;
  const cleared = clearLocalUnitsEntryFields(existing ?? {}, fieldsToClear);
  const updated: LocalUnitsEntry = {
    ...cleared,
    unitSystem: localUnitsEntry?.unitSystem ?? cleared.unitSystem
  };
  if (localUnitsEntry?.unitSystem === 'imperial') {
    changedFields.forEach((field) => {
      const key = localUnitsEntryFieldKey[field];
      const value = localUnitsEntry[key];
      if (value != null) {
        updated[key] = value;
      }
    });
  }
  item.Meta = { ...item.Meta };
  reconcileLocalUnitsEntry(item.Meta, updated);
}

export function SmartSendBulkApplier(
  filterKey: string,
  state: ISmartSendContext,
  action: BulkSaveSelectedAction,
  smartSendItemsCache: SmartSendItemApiModel[]
) {
  const force = action.payload.force;

  GetSelectedItems(filterKey, state, smartSendItemsCache).forEach((i) => {
    let unsaved = [];

    if (!action.payload.actions) {
      return;
    }

    const options = GenerateOptions(i, i.State.validation);
    const actions = action.payload.actions || [];

    const canApply = (x: ApplierOption) => options.includes(x) && actions.includes(x) && !!action.payload.changes[x];
    const canApplyWithNull = (x: ApplierOption) => options.includes(x) && actions.includes(x);
    const canApplySubAction = (category: ApplierOption, a: ApplierOption) =>
      canApplyWithNull(a) && !!action.payload.changes[category];

    if (canApplySubAction('Dimensions', 'Dimensions:Change')) {
      const changedFields: ParcelUnitField[] = [];
      if (action.payload.changes.Dimensions.Weight > 0) {
        i.PackageDimensions.Weight = action.payload.changes.Dimensions.Weight;
        unsaved.push('Dimensions');
        changedFields.push('weight');
      }
      if (action.payload.changes.Dimensions.Height > 0) {
        i.PackageDimensions.Height = action.payload.changes.Dimensions.Height;
        unsaved.push('Dimensions');
        changedFields.push('height');
      }
      if (action.payload.changes.Dimensions.Length > 0) {
        i.PackageDimensions.Length = action.payload.changes.Dimensions.Length;
        unsaved.push('Dimensions');
        changedFields.push('length');
      }
      if (action.payload.changes.Dimensions.Width > 0) {
        i.PackageDimensions.Width = action.payload.changes.Dimensions.Width;
        unsaved.push('Dimensions');
        changedFields.push('width');
      }
      applyLocalUnitsEntry(i, changedFields, action.payload.changes.LocalUnitsEntry);
      if (reflowConsignmentDimensions(i, changedFields)) {
        unsaved.push('Consignment');
      }
    } else if (canApplySubAction('Dimensions', 'Dimensions:Add')) {
      const changedFields: ParcelUnitField[] = [];
      if (!i.PackageDimensions.Weight) {
        if (action.payload.changes.Dimensions.Weight > 0) {
          i.PackageDimensions.Weight = action.payload.changes.Dimensions.Weight;
          unsaved.push('Dimensions');
          changedFields.push('weight');
        }
      }
      if (!i.PackageDimensions.Height) {
        if (action.payload.changes.Dimensions.Height > 0) {
          i.PackageDimensions.Height = action.payload.changes.Dimensions.Height;
          unsaved.push('Dimensions');
          changedFields.push('height');
        }
      }
      if (!i.PackageDimensions.Length) {
        if (action.payload.changes.Dimensions.Length > 0) {
          i.PackageDimensions.Length = action.payload.changes.Dimensions.Length;
          unsaved.push('Dimensions');
          changedFields.push('length');
        }
      }
      if (!i.PackageDimensions.Width) {
        if (action.payload.changes.Dimensions.Width > 0) {
          i.PackageDimensions.Width = action.payload.changes.Dimensions.Width;
          unsaved.push('Dimensions');
          changedFields.push('width');
        }
      }
      applyLocalUnitsEntry(i, changedFields, action.payload.changes.LocalUnitsEntry);
      if (reflowConsignmentDimensions(i, changedFields)) {
        unsaved.push('Consignment');
      }
    }

    if (canApply('Sender')) {
      unsaved.push('Sender');
      i.Sender = action.payload.changes.Sender;
    }

    if (canApplySubAction('TariffCodes', 'TariffCodes:Add')) {
      unsaved.push('MarketplaceOrderLines');
      const changes = (action.payload.changes.TariffCodes as BulkTariffCodes)?.[GetTariffSystemFromItem(i)];
      i.MarketplaceOrderLines.forEach((m) => {
        if ((!m.Product.TariffCode || force) && changes[m.Product.Description]) {
          m.Product.TariffCode = changes[m.Product.Description];
        }
      });
    }

    if (canApply('ItemValue') && i.MarketplaceOrderLines) {
      unsaved.push('ItemValue');
      i.MarketplaceOrderLines.forEach((m) => {
        if (!m.Value || force) {
          m.Value = action.payload.changes.ItemValue;
        }
      });
      i.Value = SumNumber(i.MarketplaceOrderLines.map((x) => x.Value));
    }

    if (canApplySubAction('RecipientContactDetails', 'RecipientContactDetails:Add')) {
      if (!i.Recipient.Email) {
        i.Recipient.Email = action.payload.changes.RecipientContactDetails.Email;
        unsaved.push('RecipientContactDetails');
      }
      if (!i.Recipient.Phone) {
        i.Recipient.Phone = action.payload.changes.RecipientContactDetails.Phone;
        unsaved.push('RecipientContactDetails');
      }
    } else if (canApplySubAction('RecipientContactDetails', 'RecipientContactDetails:Change')) {
      if (action.payload.changes.RecipientContactDetails.Email) {
        i.Recipient.Email = action.payload.changes.RecipientContactDetails.Email;
        unsaved.push('RecipientContactDetails');
      }
      if (action.payload.changes.RecipientContactDetails.Phone) {
        i.Recipient.Phone = action.payload.changes.RecipientContactDetails.Phone;
        unsaved.push('RecipientContactDetails');
      }
    }

    if (canApplyWithNull('PreferredCollectionDate')) {
      if (!i.PreferredCollectionDate || force) {
        unsaved.push('PreferredCollectionDate');
        i.PreferredCollectionDate = RemoveOffset(action.payload.changes.PreferredCollectionDate as Date);
      }
    }

    if (canApply('Recipient')) {
      unsaved.push('Recipient');
      i.Recipient = action.payload.changes.Recipient;
    }

    if (canApplySubAction('Service', 'Service:Add')) {
      if (!i.Service?.Id) {
        unsaved.push('Service');
        i.Service = {
          Name: action.payload.changes.Service.Name,
          Id: action.payload.changes.Service.Id,
          Provider: action.payload.changes.Service.Provider,
          AccountId: action.payload.changes.Service.AccountId,
          Meta: null
        };
      }
    } else if (canApplySubAction('Service', 'Service:Change')) {
      if (i.Service?.Id != action.payload.changes.Service?.Id) {
        unsaved.push('Service');
        i.Service = {
          Name: action.payload.changes.Service.Name,
          Id: action.payload.changes.Service.Id,
          Provider: action.payload.changes.Service.Provider,
          AccountId: action.payload.changes.Service.AccountId,
          Meta: null
        };
      }
    }
    if (canApply('ServiceOption')) {
      unsaved.push('ServiceOption');

      i.Extras = i.Extras.filter((extra) => !ProtectionExtraIds.includes(extra.Id));

      action.payload.changes.ServiceOption.Extras.filter((e) => ProtectionExtraIds.includes(e.Id)).forEach((e) => {
        if (!i.Extras.find((x) => x.Id === e.Id)) {
          i.Extras.push({ Id: e.Id, Name: e.Name, Provider: e.Provider });
        }
      });
    }

    if (canApply('Tags')) {
      unsaved.push('Tags');
      i.Tags = action.payload.changes.Tags.concat(i.Tags).filter(onlyUnique);
    }

    if (canApply('Extras') || canApply('Protection')) {
      unsaved.push('Extras');
      action.payload.changes.Extras.forEach((e) => {
        // Has Quote
        if (i.State?.validation?.QuoteState?.Quote?.Options[0]?.AvailableExtras) {
          if (
            !i.Extras.find((x) => x.Id === e.Id) &&
            i.State?.validation?.QuoteState?.Quote?.Options[0]?.AvailableExtras.find((ae) => ae.Id == e.Id)
          ) {
            i.Extras.push({ Id: e.Id, Name: e.Name, Provider: e.Provider });
          }
        } else {
          if (!i.Extras.find((x) => x.Id === e.Id)) {
            i.Extras.push({ Id: e.Id, Name: e.Name, Provider: e.Provider });
          }
        }
      });
    }

    if (canApply('Dispatch')) {
      i.MarketplaceStatus = 'Dispatched';
      unsaved.push('MarkAsDispatched');
      unsaved.push('MarketplaceStatus');
    }

    if (canApply('MarkAsPackaged')) {
      i.Meta['PackagedDate'] = { Value: new Date().toString(), Type: 'DateTime' };
      unsaved.push('MarkAsPackaged');
    }

    if (canApply('MarkAsNotPackaged')) {
      i.Meta['PackagedDate'] = { Value: null, Type: 'DateTime' };
      unsaved.push('MarkAsNotPackaged');
    }

    // Unique
    unsaved = unsaved.filter(onlyUnique);

    if (unsaved.length === 1 && unsaved[0] == 'Return') {
      // ReturnCollection Bulk Action
      i.State.unsaved = unsaved;
      i.State.state = 'RETURN';
      return;
    } else {
      // Others Bulk Actions
      if (unsaved.length > 0) {
        i.State.unsaved = unsaved;
        i.State.state = 'SAVE';
      }
    }
  });
}

export interface ProviderGroup {
  Key: string;
  Provider: { AccountId: string; Provider: string };
  Items: SmartSendItemApiModel[];
  CurrencyCode: string;
  TotalPrice: number;
  Duties: number;
  TotalPriceExVat: number;
  TotalVat: number;
  VatRate: number;
  SelectedPaymentMethod: PaymentOptionType;
  SelectedPaymentOption?: PaymentOptionOptionApiModel;
  State: ProviderGroupState;
  Summary: ProviderGroupItemSummary[];
  ShipResponse?: ShipResponse;
  MarkedAsDispatched: boolean;
  Manifestable: boolean;
  Discount: DiscountDetails;
  Links?: LinkApiModel[];
}

export interface DiscountDetails {
  TotalDiscount?: number | undefined;
  DiscountCode?: string;
  Loading?: boolean;
  Applied: boolean;
  TotalPrice?: number;
  TotalVat?: number;
  TotalPriceExVat?: number;
  Breakdown?: Record<string, number>;
}

export interface ProviderGroupItemSummary {
  Service: Service;
  Count: number;
  ItemIds: string[];
  TotalPriceExVat: number;
  TotalPrice: number;
  Duties: number;
  TotalVat: number;
  VatRate: number;
  CurrencyCode: string;
}

export type ProviderGroupState = 'BOOKING' | 'IDLE' | 'BOOKED' | 'ERROR' | 'STAGED';
